/**
 * Dropped items (vanilla ItemEntity), DOM-free.
 *
 *   const items = new ItemEntityManager();
 *   items.spawn(stack, x, y, z, vx?, vy?, vz?)        // copies `stack`; vanilla random pop when no velocity
 *   items.spawnBlockDrop(stack, bx, by, bz)            // Block.popResource: jittered inside the broken cell
 *   items.dropFromPlayer(stack, eyeX, eyeY, eyeZ, yaw, pitch)   // Q / UI drop: 0.3 along the look, 40 tick delay
 *   items.tick(host)                                   // once per 20 Hz game tick
 *   items.views()                                      // reused ItemEntityViewEx[] for ItemEntityRenderer
 *
 * Vanilla numbers: 0.25 hitbox, gravity 0.04, drag 0.98, ground friction 0.6*0.98 (ice 0.98*0.98),
 * floats in water (+5e-4 up to 0.06, x/z * 0.99), dies in lava / fire, 6000 tick life, merges with
 * a neighbouring same stack (every 2 ticks while moving, every 40 at rest), pickup when the
 * player's box inflated by (1, 0.5, 1) overlaps it and `pickupDelay` (10 for drops, 40 for
 * thrown) is spent. A picked-up item keeps existing for 3 ticks as a `collected` view that flies
 * into the player (ItemPickupParticle), so the lead only calls `tick`.
 *
 * Yaw convention: forward = (-sin yaw, 0, -cos yaw), pitch up positive.
 */

import { BLUE_ICE_ID, collisionOf, FIRE_ID, ICE_ID, IS_LAVA, IS_WATERY, NO_BOXES, PACKED_ICE_ID } from '../blocks';
import { WORLD_HEIGHT } from '../constants';
import { cloneStack, type ItemEntityView, type Stack } from '../game/types';
import { maxStackOf } from '../game/itemdb';
import { isFireproofItem } from '../game/itemUse/fireproof';
import { boxHits, clipMove, type ShapesAt } from '../world/collide';

export interface ItemEntityHost {
  getBlock(x: number, y: number, z: number): number;
  /** Receives picked-up items; returns how many did NOT fit (Inventory.add). */
  inventory: { add(id: number, count: number, dmg?: number, ench?: Stack['ench']): number };
  /** Player feet position + hitbox (defaults 0.6 x 1.8). */
  player: { x: number; y: number; z: number; width?: number; height?: number; eyeHeight?: number; alive: boolean; spectator?: boolean };
  /** 0..1 brightness at a point (sky*daylight and block light), for ItemEntityView.light. Default 1. */
  light?(x: number, y: number, z: number): number;
  /** False while the chunk at (x, z) is not loaded: the item freezes there. */
  loaded?(x: number, z: number): boolean;
  /** Called when `count` items of `stack` went into the inventory (pop sound). */
  onPickup?(stack: Stack, count: number): void;
  /** An item was destroyed by lava / fire at this point (sizzle + smoke). */
  onBurn?(x: number, y: number, z: number): void;
  /**
   * Dedicated server: every player who may pick items up. When present,
   * `player` / `inventory` are ignored, each item goes to the nearest picker
   * whose box touches it, and a fully taken item is removed at once (the
   * fly-to-player animation is the client's).
   */
  pickers?: readonly ItemPicker[];
  /** With `pickers`: `count` of item `e` went to `by`. */
  onCollect?(e: ItemEntity, by: ItemPicker, count: number): void;
}

/** One player on the server, as item pickup sees it. */
export interface ItemPicker {
  x: number; y: number; z: number;
  width?: number; height?: number;
  alive: boolean; spectator?: boolean;
  /** Take as much of `stack` as fits (keeping its name / enchantments); returns how many did NOT fit. */
  give(stack: Stack): number;
}

export interface ItemEntityViewEx extends ItemEntityView {
  /** 0 = lying in the world; 1..3 = ticks into the fly-to-player pickup animation. */
  collected: number;
}

export const ITEM_LIFETIME = 6000;
export const PICKUP_DELAY_DROP = 10;
export const PICKUP_DELAY_THROWN = 40;
const HALF = 0.125;
const HEIGHT = 0.25;
const GRAVITY = 0.04;
const EPS = 1e-4;
const MAX_ITEMS = 512;

/**
 * Every ItemEntityManager this tab has made (main.ts makes the game's one).
 * net/objects.ts mirrors the host's dropped items into the local manager and
 * needs to find it without a wiring change in main.ts, so managers register
 * themselves here. `unregister` is for tests that build throwaway managers.
 */
const live = new Set<ItemEntityManager>();

export function liveItemManagers(): readonly ItemEntityManager[] { return [...live]; }

export function unregisterItemManager(m: ItemEntityManager): void { live.delete(m); }

export class ItemEntity {
  x = 0; y = 0; z = 0;
  vx = 0; vy = 0; vz = 0;
  px = 0; py = 0; pz = 0;
  age = 0;
  pickupDelay = 10;
  onGround = false;
  /** Ticks left of the pickup animation (0 = not collected). */
  collect = 0;
  /** Where the pickup animation started. */
  sx = 0; sy = 0; sz = 0;
  burn = 0;
  readonly view: ItemEntityViewEx;
  constructor(readonly id: number, public stack: Stack) {
    this.view = {
      id, stack, x: 0, y: 0, z: 0, px: 0, py: 0, pz: 0, age: 0, light: 1, collected: 0,
    };
  }
  get removed(): boolean { return this.stack.count <= 0; }
}

export class ItemEntityManager {
  readonly items: ItemEntity[] = [];
  private nextId = 1;
  private tickCount = 0;
  private readonly rnd: () => number;
  private readonly box = [0, 0, 0, 0, 0, 0];
  private readonly viewList: ItemEntityViewEx[] = [];
  private host!: ItemEntityHost;
  private readonly shapesAt: ShapesAt = (x, y, z) =>
    y < 0 || y >= WORLD_HEIGHT ? NO_BOXES : collisionOf(this.host.getBlock(x, y, z));

  constructor(opts: { rnd?: () => number; maxItems?: number; allocId?: () => number } = {}) {
    this.rnd = opts.rnd ?? Math.random;
    this.maxItems = opts.maxItems ?? MAX_ITEMS;
    if (opts.allocId) this.allocId = opts.allocId;
    live.add(this);
  }
  private readonly maxItems: number;
  /** Entity ids: own counter, or the server's shared one (mobs, items and players share an id space there). */
  private allocId: () => number = () => this.nextId++;

  get count(): number { return this.items.length; }

  /** Drop a copy of `stack` at (x, y, z); (x, y, z) is the item's feet, i.e. its box bottom centre. */
  spawn(stack: Stack, x: number, y: number, z: number, vx?: number, vy?: number, vz?: number, pickupDelay = PICKUP_DELAY_DROP): ItemEntity | null {
    if (stack.id <= 0 || stack.count <= 0) return null;
    const e = new ItemEntity(this.allocId(), cloneStack(stack));
    e.view.stack = e.stack;
    e.x = e.px = x; e.y = e.py = y; e.z = e.pz = z;
    e.vx = vx ?? this.rnd() * 0.2 - 0.1;
    e.vy = vy ?? 0.2;
    e.vz = vz ?? this.rnd() * 0.2 - 0.1;
    e.pickupDelay = pickupDelay;
    this.items.push(e);
    if (this.items.length > this.maxItems) this.items.shift();
    return e;
  }

  /** Block.popResource: somewhere in the middle half of the broken cell, popping up. */
  spawnBlockDrop(stack: Stack, bx: number, by: number, bz: number): ItemEntity | null {
    return this.spawn(
      stack,
      bx + 0.5 + (this.rnd() * 0.5 - 0.25),
      by + 0.5 + (this.rnd() * 0.5 - 0.25) - HEIGHT / 2,
      bz + 0.5 + (this.rnd() * 0.5 - 0.25),
    );
  }

  /** Player.drop(): out of the eyes (0.3 below), thrown along the look with a little scatter. */
  dropFromPlayer(stack: Stack, eyeX: number, eyeY: number, eyeZ: number, yaw: number, pitch: number): ItemEntity | null {
    const cp = Math.cos(pitch);
    const a = this.rnd() * Math.PI * 2;
    const k = 0.02 * this.rnd();
    return this.spawn(
      stack, eyeX, eyeY - 0.3 - HEIGHT / 2, eyeZ,
      -Math.sin(yaw) * cp * 0.3 + Math.cos(a) * k,
      Math.sin(pitch) * 0.3 + 0.1 + (this.rnd() - this.rnd()) * 0.1,
      -Math.cos(yaw) * cp * 0.3 + Math.sin(a) * k,
      PICKUP_DELAY_THROWN,
    );
  }

  /** One 20 Hz game tick. */
  tick(host: ItemEntityHost): void {
    this.host = host;
    this.tickCount++;
    const p = host.player;
    const pw = (p.width ?? 0.6) / 2 + 1;
    const ph = p.height ?? 1.8;
    const eye = p.eyeHeight ?? ph * 0.9;
    for (let i = this.items.length - 1; i >= 0; i--) {
      const e = this.items[i];
      e.px = e.x; e.py = e.y; e.pz = e.z;
      if (e.collect > 0) { this.tickCollect(e, p, eye); if (e.collect <= 0) this.items.splice(i, 1); continue; }
      if (host.loaded && !host.loaded(e.x, e.z)) continue;
      this.step(e);
      if (e.removed || e.y < -64 || e.age >= ITEM_LIFETIME || e.burn >= 2) {
        if (e.burn >= 2) host.onBurn?.(e.x, e.y + HEIGHT / 2, e.z);
        this.items.splice(i, 1);
        continue;
      }
      if (host.pickers) {
        if (e.pickupDelay <= 0 && this.pickupBy(e, host, host.pickers)) this.items.splice(i, 1);
        continue;
      }
      // pickup: the player's box, inflated by (1, 0.5, 1), touches the item's
      if (e.pickupDelay <= 0 && p.alive && !p.spectator
        && Math.abs(e.x - p.x) < pw + HALF && Math.abs(e.z - p.z) < pw + HALF
        && e.y < p.y + ph + 0.5 && e.y + HEIGHT > p.y - 0.5) {
        const n = e.stack.count;
        const left = host.inventory.add(e.stack.id, n, e.stack.dmg, e.stack.ench);
        if (left < n) {
          host.onPickup?.(e.stack, n - left);
          if (left <= 0) {
            e.collect = 3; e.sx = e.x; e.sy = e.y; e.sz = e.z;
          } else e.stack.count = left;
        }
      }
    }
    this.refreshViews(host);
  }

  /** Reused array of render views (rebuilt each tick). */
  views(): readonly ItemEntityViewEx[] { return this.viewList; }

  clear(): void { this.items.length = 0; this.viewList.length = 0; }

  /** Flat save payload: [id, count, dmg, x, y, z, age] per item. */
  serialize(): number[] {
    const out: number[] = [];
    for (const e of this.items) if (e.collect <= 0) out.push(e.stack.id, e.stack.count, e.stack.dmg, e.x, e.y, e.z, e.age);
    return out;
  }

  load(flat: readonly number[] | undefined): void {
    this.clear();
    if (!flat) return;
    for (let i = 0; i + 6 < flat.length; i += 7) {
      const e = this.spawn({ id: flat[i], count: flat[i + 1], dmg: flat[i + 2] }, flat[i + 3], flat[i + 4], flat[i + 5], 0, 0, 0, 0);
      if (e) e.age = flat[i + 6];
    }
  }

  // --- internals -----------------------------------------------------------

  /** Server pickup: nearest touching picker first. True when the whole stack was taken. */
  private pickupBy(e: ItemEntity, host: ItemEntityHost, pickers: readonly ItemPicker[]): boolean {
    let tried: ItemPicker[] | null = null;
    for (;;) {
      let best: ItemPicker | null = null, bd = Infinity;
      for (const p of pickers) {
        if (!p.alive || p.spectator || tried?.includes(p)) continue;
        const reach = (p.width ?? 0.6) / 2 + 1 + HALF;
        const dx = e.x - p.x, dz = e.z - p.z;
        if (Math.abs(dx) >= reach || Math.abs(dz) >= reach) continue;
        if (e.y >= p.y + (p.height ?? 1.8) + 0.5 || e.y + HEIGHT <= p.y - 0.5) continue;
        const d = dx * dx + dz * dz;
        if (d < bd) { bd = d; best = p; }
      }
      if (!best) return false;
      const n = e.stack.count;
      const left = best.give(e.stack);
      if (left < n) {
        host.onCollect?.(e, best, n - left);
        if (left <= 0) return true;
        e.stack.count = left;
      }
      (tried ??= []).push(best);
    }
  }

  private tickCollect(e: ItemEntity, p: ItemEntityHost['player'], eye: number): void {
    e.collect--;
    const k = (3 - e.collect) / 3;
    const f = k * k;
    e.x = e.sx + (p.x - e.sx) * f;
    e.y = e.sy + (p.y + eye / 2 - HEIGHT / 2 - e.sy) * f;
    e.z = e.sz + (p.z - e.sz) * f;
    e.age++;
  }

  private waterDepth(e: ItemEntity): number {
    const h = this.host;
    const bx = Math.floor(e.x), bz = Math.floor(e.z);
    const by = Math.floor(e.y + 0.02);
    if (by < 0 || by >= WORLD_HEIGHT || !IS_WATERY[h.getBlock(bx, by, bz)]) return 0;
    // another water cell above: the item is fully under; else the surface sits 1/9 below the cell top
    if (by + 1 < WORLD_HEIGHT && IS_WATERY[h.getBlock(bx, by + 1, bz)]) return 1;
    return Math.min(1, by + 0.889 - e.y);
  }

  private step(e: ItemEntity): void {
    const h = this.host;
    if (e.pickupDelay > 0) e.pickupDelay--;
    const cx = Math.floor(e.x), cy = Math.floor(e.y + 0.05), cz = Math.floor(e.z);
    const here = cy < 0 || cy >= WORLD_HEIGHT ? 0 : h.getBlock(cx, cy, cz);
    if ((IS_LAVA[here] === 1 || here === FIRE_ID) && !isFireproofItem(e.stack.id)) e.burn++;

    const depth = this.waterDepth(e);
    if (IS_LAVA[here] === 1) {
      if (e.vy < 0.06) e.vy += 5e-4;
      e.vx *= 0.95; e.vz *= 0.95;
    } else if (depth > 0.1) {
      if (e.vy < 0.06) e.vy += 5e-4;
      e.vx *= 0.99; e.vz *= 0.99;
    } else {
      e.vy -= GRAVITY;
    }

    const box = this.box;
    const set = (): void => {
      box[0] = e.x - HALF; box[1] = e.y; box[2] = e.z - HALF;
      box[3] = e.x + HALF; box[4] = e.y + HEIGHT; box[5] = e.z + HALF;
    };
    set();
    // buried in a block (a pile of stone placed on it): ease out through the top
    if (boxHits(box, EPS, this.shapesAt)) {
      e.y += 0.1; e.vx = e.vz = 0; e.vy = 0;
    } else if (!e.onGround || e.vx * e.vx + e.vz * e.vz > 1e-5 || (this.tickCount + e.id) % 4 === 0) {
      this.move(e, box);
      let f = 0.98;
      if (e.onGround) {
        const below = this.host.getBlock(Math.floor(e.x), Math.floor(e.y - 0.5), Math.floor(e.z));
        f = (below === ICE_ID || below === PACKED_ICE_ID ? 0.98 : below === BLUE_ICE_ID ? 0.989 : 0.6) * 0.98;
      }
      e.vx *= f; e.vy *= 0.98; e.vz *= f;
    }

    // merge with a neighbouring same stack: often while it moves, rarely at rest
    const moved = Math.floor(e.px) !== Math.floor(e.x) || Math.floor(e.py) !== Math.floor(e.y) || Math.floor(e.pz) !== Math.floor(e.z);
    if (this.tickCount % (moved ? 2 : 40) === 0 && e.age < ITEM_LIFETIME && e.stack.count < maxStackOf(e.stack.id)) this.merge(e);
    e.age++;
  }

  /** Entity.move: y first, then the longer horizontal axis. */
  private move(e: ItemEntity, box: number[]): void {
    const shapes = this.shapesAt;
    let dy = e.vy;
    const cy = clipMove(box, 1, dy, EPS, shapes);
    box[1] += cy; box[4] += cy;
    e.onGround = dy < 0 && cy > dy;
    if (cy !== dy) e.vy = 0;
    dy = cy;
    const order = Math.abs(e.vx) >= Math.abs(e.vz) ? [0, 2] : [2, 0];
    for (const axis of order) {
      const want = axis === 0 ? e.vx : e.vz;
      const c = clipMove(box, axis, want, EPS, shapes);
      box[axis] += c; box[axis + 3] += c;
      if (c !== want) { if (axis === 0) e.vx = 0; else e.vz = 0; }
    }
    e.x = (box[0] + box[3]) / 2; e.y = box[1]; e.z = (box[2] + box[5]) / 2;
  }

  private merge(e: ItemEntity): void {
    for (const o of this.items) {
      if (o === e || o.collect > 0 || o.removed || o.age >= ITEM_LIFETIME) continue;
      if (o.stack.id !== e.stack.id || o.stack.dmg !== e.stack.dmg || o.stack.ench || e.stack.ench) continue;
      if (Math.abs(o.x - e.x) > 0.5 + 2 * HALF || Math.abs(o.z - e.z) > 0.5 + 2 * HALF || Math.abs(o.y - e.y) > HEIGHT) continue;
      const max = maxStackOf(e.stack.id);
      if (o.stack.count >= max) continue;
      // the smaller stack flows into the larger one (ItemEntity.tryToMerge)
      const [from, into] = o.stack.count < e.stack.count ? [o, e] : [e, o];
      const n = Math.min(max - into.stack.count, from.stack.count);
      into.stack.count += n;
      from.stack.count -= n;
      into.pickupDelay = Math.max(into.pickupDelay, from.pickupDelay);
      into.age = Math.min(into.age, from.age);
      if (from === e) return;
    }
  }

  private refreshViews(host: ItemEntityHost): void {
    const out = this.viewList;
    out.length = 0;
    for (const e of this.items) {
      if (e.removed) continue;
      const v = e.view;
      v.stack = e.stack;
      v.x = e.x; v.y = e.y; v.z = e.z;
      v.px = e.px; v.py = e.py; v.pz = e.pz;
      v.age = e.age;
      v.collected = e.collect > 0 ? 4 - e.collect : 0;
      v.light = host.light ? host.light(e.x, e.y + 0.1, e.z) : 1;
      out.push(v);
    }
  }
}
